import { reactMount } from '@/lib/reactMount'
import { useRef } from 'react'
import { Button } from '@/components/Button'
import { Tag } from '@/components/Tag'
import { ArrowRight } from '@/components/Icons'
import { getHeadingClasses } from '@/lib/headings'

export const cardBlock = ({ dataset, umbraco }) => {
  const umbracoRef = useRef(umbraco || JSON.parse(dataset.umbraco))
  umbraco ||= umbracoRef.current
  const theme = umbraco.Theme || 'light'
  const backgroundColor = umbraco.BlockBackground ? `#${umbraco.BlockBackground}` : '#ffffff'

  const itemCount = umbraco.Blocks.length
  let itemCountClasses = ''

  if (itemCount === 4) itemCountClasses = 'w-full grid md:grid-cols-2 lg:grid-cols-4'
  if (itemCount === 3) itemCountClasses = 'w-full grid md:grid-cols-3'
  if (itemCount === 2) itemCountClasses = 'w-full grid md:grid-cols-2'

  // Button logic matching standaloneCtaBlock pattern
  const primary = umbraco.Button
  const secondary = umbraco.SecondaryButton
  const tertiary = umbraco.TertiaryButton
  const hyperlink = umbraco.ButtonHyperlink

  const buttons = []

  // Primary
  if (primary) {
    buttons.push({
      ...primary,
      variant: 'primary',
      size: 'lg'
    })
  }

  // Secondary preferred over tertiary
  if (secondary) {
    buttons.push({
      ...secondary,
      variant: 'secondary',
      size: 'lg'
    })
  } else if (tertiary) {
    buttons.push({
      ...tertiary,
      variant: 'primary-alt',
      size: 'lg'
    })
  }
  // Hyperlink only if nothing else exists
  else if (hyperlink) {
    buttons.push({
      ...hyperlink,
      variant: 'link',
      size: 'lg'
    })
  }
  // Default to <h2>; promote to <h1> only when the server marks this block as first on the page.
  const isFirstBlockRaw = dataset?.isFirstBlock
  const Heading = isFirstBlockRaw === undefined
    ? 'h2'
    : (isFirstBlockRaw === 'true' ? 'h1' : 'h2')

  const headingClasses = getHeadingClasses(umbraco.TitleHeadingSize, { fallback: 'small' })

  return (
    <div data-theme={theme} className='_block-section _cardBlock-section relative isolate py-12 md:px-4 lg:px-10 xl:px-24' style={{ backgroundColor }}>
      <div className='text-center mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        {
          umbraco.Title &&
            <Heading className={`font-extrabold ${headingClasses} mb-2 lg:mb-4 tracking-tight text-[--theme-highlight] text-pretty`}>{umbraco.Title}</Heading>
        }
        {
          umbraco.SubTitle &&
            <h3 className='font-extrabold text-lg md:text-xl lg:text-2xl tracking-tight text-[--theme-text-alt-3] text-pretty'>{umbraco.SubTitle}</h3>
        }
        <div
          className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-pretty text-base font-semibold mt-5 first:mt-0'
          dangerouslySetInnerHTML={{ __html: umbraco.Summary }}
        />
      </div>
      <div className={`mx-auto max-w-[1464px] px-4 xl:px-3 pt-10 isolate gap-4 ${itemCountClasses}`}>
        {umbraco.Blocks.map((block, index) => {
          const TagName = block.Button?.Url ? 'a' : 'div'
          const tagProps = block.Button?.Url
            ? { href: block.Button.Url, target: block.Button.Target || '_self' }
            : {}

          return (
            <TagName
              key={`card-block-${index}`}
              className='group relative text-[--theme-text]'
              {...tagProps}
            >
              <div className='h-[400px] relative rounded-[20px] overflow-hidden'>
                {
                  block.Media && block.Media.Image &&
                    <img className='absolute inset-0 object-cover w-full h-full' src={block.Media.Image.Url} alt={block.Media.Image.AltText} />
                }
                <div className='absolute inset-0 bg-black opacity-10' />
                {block.Label &&
                  <Tag className='absolute top-4 left-4 z-10'>{block.Label}</Tag>}
              </div>
              <div className={`px-2 inset-0 flex flex-col ${block.Title ? 'py-4' : ''}`}>
                <h3 id={`card-block-${index}`} className='text-xl text-primary-900 font-extrabold line-clamp-3'>
                  {block.Title}
                </h3>
                {
                  block.Button &&
                    <div data-theme='blue' className='grow flex items-end'>
                      <Button
                        noInteraction
                        variant='link'
                      >
                        {block.Button?.Name || 'Learn more'}
                      </Button>
                    </div>
                }
              </div>
            </TagName>
          )
        })}
      </div>
      {buttons.length > 0 &&
        <div className='px-4 pt-8 sm:px-6 lg:px-8 isolate gap-y-2 gap-x-6 justify-center flex md:flex-row flex-wrap'>
          {buttons.map((btn, index) => (
            <Button
              key={index}
              href={btn.Url}
              target={btn.Target}
              variant={btn.variant}
              size={btn.size}
              componentName={btn.Url?.includes('#apply-dialog') ? 'Card Block' : null}
            >
              {btn.variant === 'primary-alt'
                ? (
                  <span className='block relative pr-7'>{btn.Name} <ArrowRight className='absolute -right-0.5 top-1/2 -mt-[11px]' /></span>
                  )
                : (
                    btn.Name
                  )}
            </Button>
          ))}
        </div>}
    </div>
  )
}

reactMount('cardBlock', cardBlock)
